<template>
  <div class="how-to-play">
    <SectionHeader
      :title="$t('howToPlay.title')"
      :subtitle="$t('howToPlay.subtitle')"
      variant="complex"
    />

    <div class="sections">
      <!-- Step 1: Select Your Game -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <div
              v-if="isLite"
              class="image-placeholder"
              aria-hidden="true"
            />
            <img
              v-else
              :src="howToPlayStep1"
              alt="Select Your Game"
            >
          </div>

          <div class="text-content">
            <h3>{{ $t('howToPlay.step1Title') }}</h3>
            <p>{{ $t('howToPlay.step1Text1') }}</p>
            <p>{{ $t('howToPlay.step1Text2') }}</p>
            <p>{{ $t('howToPlay.step1Text3') }}</p>
          </div>
        </div>
      </section>

      <!-- Step 2: Select Draws -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>{{ $t('howToPlay.step2Title') }}</h3>
            <p>{{ $t('howToPlay.step2Text1') }}</p>
            <p>{{ $t('howToPlay.step2Text2') }}</p>
            <p>{{ $t('howToPlay.step2Text3') }}</p>
          </div>
          <div class="image-content">
            <div
              v-if="isLite"
              class="image-placeholder"
              aria-hidden="true"
            />
            <img
              v-else
              :src="howToPlayStep2"
              alt="Select Your Draws"
            >
          </div>
        </div>
      </section>

      <!-- Step 3: Select Market -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <div
              v-if="isLite"
              class="image-placeholder"
              aria-hidden="true"
            />
            <img
              v-else
              :src="howToPlayStep3"
              alt="Select Your Market"
            >
          </div>
          <div class="text-content">
            <h3>{{ $t('howToPlay.step3Title') }}</h3>
            <p>{{ $t('howToPlay.step3Text1') }}</p>
            <p>{{ $t('howToPlay.step3Text2') }}</p>
            <p>{{ $t('howToPlay.step3Text3') }}</p>
          </div>
        </div>
      </section>

      <!-- Step 4: Confirm Your Entries -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>{{ $t('howToPlay.step4Title') }}</h3>
            <p>{{ $t('howToPlay.step4Text1') }}</p>
            <p>{{ $t('howToPlay.step4Text2') }}</p>
            <p>{{ $t('howToPlay.step4Text3') }}</p>
          </div>
          <div class="image-content">
            <div
              v-if="isLite"
              class="image-placeholder"
              aria-hidden="true"
            />
            <img
              v-else
              :src="howToPlayStep4"
              alt="Confirm Your Entries"
            >
          </div>
        </div>
      </section>

      <!-- Step 5: Your Betslip -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <div
              v-if="isLite"
              class="image-placeholder"
              aria-hidden="true"
            />
            <img
              v-else
              :src="howToPlayStep5"
              alt="Your Betslip"
            >
          </div>
          <div class="text-content">
            <h3>{{ $t('howToPlay.step5Title') }}</h3>
            <p>{{ $t('howToPlay.step5Text1') }}</p>
            <p>{{ $t('howToPlay.step5Text2') }}</p>
            <p>{{ $t('howToPlay.step5Text3') }}</p>
            <p
              v-if="showBookABetNote"
              class="book-bet-note"
            >
              {{ $t('howToPlay.step5BookBetNote') }}
            </p>
          </div>
        </div>
      </section>

      <!-- Step 6: Wager Details -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>{{ $t('howToPlay.step6Title') }}</h3>
            <p>{{ $t('howToPlay.step6Text1') }}</p>
            <p>{{ $t('howToPlay.step6Text2') }}</p>
            <p>{{ $t('howToPlay.step6Text3') }}</p>
          </div>
          <div class="image-content">
            <div
              v-if="isLite"
              class="image-placeholder"
              aria-hidden="true"
            />
            <img
              v-else
              :src="howToPlayStep6"
              alt="Wager Details"
            >
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { defineOptions } from 'vue'
import SectionHeader from '@/components/base/SectionHeader.vue'
import { useTheme } from '@/composables/useTheme'
import { useGameStore } from '@/stores/game'
import { useIntegrationMode } from '@/composables/useIntegrationMode'

defineOptions({
  name: 'HowToPlayView'
})

const { getAssetPath } = useTheme()
const gameStore = useGameStore()
const { isLite } = useIntegrationMode()

const showBookABetNote = computed(() => {
  const modules = gameStore.getBetSettings?.enabled_modules
  return Array.isArray(modules) && modules.includes('book_a_bet')
})

const howToPlayStep1 = computed(() => getAssetPath('howToPlay', 'step1') || '/img/images/how-to-play/1-select-game.png')
const howToPlayStep2 = computed(() => getAssetPath('howToPlay', 'step2') || '/img/images/how-to-play/2-select-draws.png')
const howToPlayStep3 = computed(() => getAssetPath('howToPlay', 'step3') || '/img/images/how-to-play/3-select-market.png')
const howToPlayStep4 = computed(() => getAssetPath('howToPlay', 'step4') || '/img/images/how-to-play/4-confirm-your-entries.png')
const howToPlayStep5 = computed(() => getAssetPath('howToPlay', 'step5') || '/img/images/how-to-play/5-your-betslip.png')
const howToPlayStep6 = computed(() => getAssetPath('howToPlay', 'step6') || '/img/images/how-to-play/6-wager-details.png')
</script>

<style scoped>
.how-to-play {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: var(--how-to-play-page-padding, 5px);
  box-sizing: border-box;
  background: var(--background);
}

.how-to-play :deep(.section-header.complex) {
  margin-bottom: 20px;
  padding: 0 4px;
}

.sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.how-to-play .instruction-section {
  background: var(--bg-instruction-section, var(--colour-surface-cont-lowest));
  border: 1px solid var(--colour-surface-cont);
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  margin: 0;
}

.how-to-play .instruction-section h3 {
  color: var(--text-instruction-heading, var(--colour-base-text-colour));
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 16px;
}

.how-to-play .section-content {
  display: grid;
  gap: 22px;
  align-items: start;
}

.how-to-play .section-content.image-first {
  grid-template-columns: minmax(0, 30%) minmax(0, 70%);
}

.how-to-play .section-content.image-last {
  grid-template-columns: minmax(0, 70%) minmax(0, 30%);
}

.how-to-play .text-content {
  color: var(--text-instruction-body, var(--colour-secondary-text-colour));
  text-align: left;
  padding: 20px;
}

.how-to-play .text-content p {
  margin: 0 0 14px;
  line-height: 1.6;
  font-size: 15px;
}

.how-to-play .text-content p:last-child {
  margin-bottom: 0;
}

.how-to-play .book-bet-note {
  margin: 16px 0 0;
  padding: 14px 16px;
  border-radius: 8px;
  background: var(--how-to-play-note-bg, color-mix(in srgb, var(--colour-yellow-text-colour) 10%, transparent));
  border: 1px solid var(--how-to-play-note-border, color-mix(in srgb, var(--colour-yellow-text-colour) 45%, transparent));
  color: var(--how-to-play-note-color, var(--colour-base-text-colour));
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
}

.how-to-play .image-content {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-height: 180px;
  padding: 24px;
  background: var(--bg-instruction-image, var(--colour-surface-cont));
  overflow: hidden;
}

.how-to-play .image-content img {
  width: 100%;
  max-width: 260px;
  max-height: 320px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--shadow-color);
  display: block;
  margin: 0;
}

.how-to-play .image-placeholder {
  width: 100%;
  max-width: 260px;
  min-height: 220px;
  aspect-ratio: 13 / 16;
  border-radius: 8px;
  background: var(--bg-instruction-image-placeholder, var(--colour-surface-cont));
  border: 1px solid var(--colour-surface-cont-high, var(--colour-surface-cont));
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .how-to-play {
    padding: var(--how-to-play-page-mobile-padding, 0 4px 16px);
  }

  .how-to-play :deep(.section-header.complex) {
    margin-bottom: 16px;
    padding: 0 8px;
    gap: 8px;
  }

  .how-to-play :deep(.section-header.complex .title) {
    font-size: 22px;
  }

  .how-to-play :deep(.section-header.complex .subtitle) {
    font-size: 14px;
    line-height: 1.5;
  }

  .sections {
    gap: 10px;
  }

  .how-to-play .section-content {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .how-to-play .text-content {
    display: contents;
  }

  .how-to-play .instruction-section h3 {
    order: 1;
    font-size: 16px;
    font-weight: 700;
    margin: 0;
    padding: 14px 16px 10px;
    color: var(--text-instruction-heading, var(--colour-base-text-colour));
  }

  .how-to-play .image-content {
    order: 2;
    width: 100%;
    min-height: 0;
    margin: 0;
    padding: 12px 16px 16px;
    background: var(--bg-instruction-image, var(--colour-surface-cont));
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
  }

  .how-to-play .image-content img {
    width: auto;
    max-width: min(100%, 280px);
    max-height: none;
    margin: 0 auto;
    box-shadow: none;
    border-radius: 6px;
    object-fit: contain;
  }

  .how-to-play .image-placeholder {
    width: 100%;
    max-width: 100%;
    min-height: 180px;
    aspect-ratio: 16 / 10;
    border-radius: 6px;
  }

  .how-to-play .text-content p {
    order: 3;
    margin: 0;
    padding: 0 16px 12px;
    font-size: 14px;
    line-height: 1.55;
    color: var(--text-instruction-body, var(--colour-base-text-colour));
  }

  .how-to-play .text-content p:last-of-type {
    padding-bottom: 16px;
  }

  .how-to-play .book-bet-note {
    order: 4;
    margin: 0 16px 16px;
    padding: 12px 14px;
    font-size: 13px;
  }

  .how-to-play .section-content.image-last .image-content,
  .how-to-play .section-content.image-first .image-content {
    order: 2;
  }
}
</style>
